/**
 * MidiEngine — wraps the Web MIDI API.
 *
 * States:
 *   - 'unsupported' — navigator.requestMIDIAccess doesn't exist (Firefox on
 *     non-secure contexts, some mobile browsers).
 *   - 'pending' — init() is in flight; we're waiting on the user's
 *     permission prompt.
 *   - 'denied' — user (or system) refused MIDI access.
 *   - 'ready' — MIDIAccess is live; we're watching plug/unplug events and
 *     routing the active device's messages to our callbacks.
 *
 * Active input modes:
 *   - 'none' — ignore all incoming MIDI (useful if the user has a controller
 *     connected but doesn't want it driving the synth).
 *   - 'all' — subscribe to every input. Messages from any device route through.
 *   - <device id> — subscribe to one specific device.
 *
 * The engine is deliberately decoupled from the voice/audio layer: it calls
 * back via `onMessage` with a typed MidiMessage and lets main.ts decide what
 * to do with each kind. That keeps the wiring explicit and testable.
 */

import { parseMidiMessage, type MidiMessage } from './messages';

export type MidiEngineState =
  | { kind: 'unsupported' }
  | { kind: 'pending' }
  | { kind: 'denied'; error?: string }
  | { kind: 'ready' };

export type ActiveInput = 'none' | 'all' | string; // device id

export interface MidiInputInfo {
  readonly id: string;
  readonly name: string;
  readonly manufacturer?: string;
}

export type StateListener = (state: MidiEngineState) => void;
export type InputListListener = (inputs: readonly MidiInputInfo[]) => void;
export type MessageListener = (message: MidiMessage, inputId: string) => void;

export class MidiEngine {
  private state: MidiEngineState = { kind: 'pending' };
  private access: MIDIAccess | null = null;
  private activeInput: ActiveInput = 'all';

  private readonly stateListeners = new Set<StateListener>();
  private readonly inputListeners = new Set<InputListListener>();
  private readonly messageListeners = new Set<MessageListener>();

  /** Web-MIDI MIDIInput listeners keyed by input id so we can detach on swap. */
  private readonly wiredInputs = new Map<string, MIDIInput>();

  get currentState(): MidiEngineState {
    return this.state;
  }

  get currentActiveInput(): ActiveInput {
    return this.activeInput;
  }

  /**
   * Request MIDI access. Must be called in response to a user gesture on
   * some browsers; fine to call on page load on others. Safe to re-call.
   */
  async init(): Promise<MidiEngineState> {
    if (this.state.kind === 'ready') return this.state;
    if (typeof navigator === 'undefined' || !('requestMIDIAccess' in navigator)) {
      this.setState({ kind: 'unsupported' });
      return this.state;
    }
    this.setState({ kind: 'pending' });
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ kind: 'denied', error: message });
      return this.state;
    }
    this.access.onstatechange = () => {
      this.emitInputs();
      this.applyActiveInput();
    };
    this.setState({ kind: 'ready' });
    this.emitInputs();
    this.applyActiveInput();
    return this.state;
  }

  /** Current input devices. Empty array until `init()` succeeds. */
  listInputs(): MidiInputInfo[] {
    if (!this.access) return [];
    const out: MidiInputInfo[] = [];
    for (const input of this.access.inputs.values()) {
      const info: MidiInputInfo = {
        id: input.id,
        name: input.name ?? 'Unknown input',
        ...(input.manufacturer ? { manufacturer: input.manufacturer } : {}),
      };
      out.push(info);
    }
    return out;
  }

  /** Switch which input(s) are listened to. */
  setActiveInput(active: ActiveInput): void {
    if (this.activeInput === active) return;
    this.activeInput = active;
    this.applyActiveInput();
  }

  // ── Subscriptions ───────────────────────────────────────────────────

  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  onInputsChange(listener: InputListListener): () => void {
    this.inputListeners.add(listener);
    listener(this.listInputs());
    return () => {
      this.inputListeners.delete(listener);
    };
  }

  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => {
      this.messageListeners.delete(listener);
    };
  }

  dispose(): void {
    for (const input of this.wiredInputs.values()) input.onmidimessage = null;
    this.wiredInputs.clear();
    if (this.access) this.access.onstatechange = null;
    this.stateListeners.clear();
    this.inputListeners.clear();
    this.messageListeners.clear();
  }

  // ── Internals ───────────────────────────────────────────────────────

  private setState(next: MidiEngineState): void {
    this.state = next;
    for (const fn of this.stateListeners) fn(next);
  }

  private emitInputs(): void {
    const list = this.listInputs();
    for (const fn of this.inputListeners) fn(list);
  }

  /**
   * Attach or detach `onmidimessage` handlers so the currently-active input
   * selection routes to our dispatch, and everything else is silent.
   */
  private applyActiveInput(): void {
    if (!this.access) return;

    const wanted = new Set<string>();
    if (this.activeInput === 'all') {
      for (const input of this.access.inputs.values()) wanted.add(input.id);
    } else if (this.activeInput !== 'none') {
      const input = this.access.inputs.get(this.activeInput);
      if (input) wanted.add(input.id);
    }

    // Detach any no-longer-wanted inputs.
    for (const [id, input] of this.wiredInputs) {
      if (!wanted.has(id)) {
        input.onmidimessage = null;
        this.wiredInputs.delete(id);
      }
    }
    // Attach any newly-wanted inputs.
    for (const id of wanted) {
      if (this.wiredInputs.has(id)) continue;
      const input = this.access.inputs.get(id);
      if (!input) continue;
      input.onmidimessage = (event) => this.handleMessage(event, id);
      this.wiredInputs.set(id, input);
    }
  }

  private handleMessage(event: MIDIMessageEvent, inputId: string): void {
    // `event.data` is a Uint8Array in the Web MIDI spec, but we defensively
    // tolerate any iterable-of-bytes. parseMidiMessage returns null for
    // anything we don't care about (system messages, SysEx, etc.).
    const data = event.data;
    if (!data) return;
    const msg = parseMidiMessage(data);
    if (!msg) return;
    for (const fn of this.messageListeners) fn(msg, inputId);
  }
}
